import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Bot, Eye, EyeOff, FolderOpen, Lock, MessageSquare, Paperclip } from 'lucide-react';
import Modal from '../Modal';
import { ticketsApi, type AiDispatchSelection } from '../../api/tickets';
import { aiDispatchReposApi } from '../../api/aiDispatchRepos';
import { productLinesApi } from '../../api/productLines';
import type { Ticket, TicketMessage } from '../../types';

// ─────────────────────────────────────────────────────────────
// Send to Claude — part picker + machine picker in one dialog.
// Everything starts checked (the previous behavior); unchecking
// trims what the dispatched prompt contains. The description is
// always included — it IS the report being dispatched.
// ─────────────────────────────────────────────────────────────

interface SendToClaudeModalProps {
  ticketId: string;
  ticket: Ticket;
  messages: TicketMessage[];
  onClose: () => void;
}

interface AttachmentRow {
  id: string;
  filename: string;
  size: number;
  origin: string; // "ticket" | "reply" | "internal note"
}

function fmtSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function snippet(text: string, max = 90): string {
  const flat = (text || '').replace(/!\[[^\]]*\]\([^)]*\)/g, '[image]').replace(/\s+/g, ' ').trim();
  return flat.length > max ? `${flat.slice(0, max)}…` : flat;
}

function msgId(m: TicketMessage): string {
  return (m as any).id ?? m._id;
}

export default function SendToClaudeModal({ ticketId, ticket, messages, onClose }: SendToClaudeModalProps) {
  const t = ticket as any;

  // Which named dispatch agents are online right now (fresh per open).
  const { data: agentData, isLoading: agentsLoading } = useQuery({
    queryKey: ['ai-dispatch-agents'],
    queryFn: () => ticketsApi.aiDispatchAgents(),
  });
  const agents = agentData?.agents ?? [];

  // Destination data: the company's product lines (to re-file a mislabeled
  // ticket at dispatch time) and each agent's repo mappings + folder inventory
  // (to show — and override — where the ticket will open).
  const { data: productLines } = useQuery({
    queryKey: ['product-lines'],
    queryFn: () => productLinesApi.list(),
  });
  const { data: repoData } = useQuery({
    queryKey: ['ai-dispatch-repos'],
    queryFn: () => aiDispatchReposApi.list(),
  });

  const factFields = [t.productVersion, t.environment, t.operatingSystem, t.browser, t.device, t.reproducibility, t.impact];
  const hasFacts = factFields.some((v: unknown) => typeof v === 'string' && v.trim());
  const hasSteps = typeof t.stepsToReproduce === 'string' && t.stepsToReproduce.trim();
  const hasResolution = typeof t.resolution === 'string' && t.resolution.trim();

  const attachmentRows: AttachmentRow[] = useMemo(() => {
    const rows: AttachmentRow[] = [];
    for (const a of (t.attachments ?? []) as any[]) {
      if (a?.id) rows.push({ id: a.id, filename: a.filename, size: a.size, origin: 'ticket' });
    }
    for (const m of messages) {
      const origin = m.type === 'internal_note' ? 'internal note' : 'reply';
      for (const a of (m.attachments ?? []) as any[]) {
        if (a?.id) rows.push({ id: a.id, filename: a.filename, size: a.size, origin });
      }
    }
    return rows;
  }, [t.attachments, messages]);

  const [includeFacts, setIncludeFacts] = useState(true);
  const [includeSteps, setIncludeSteps] = useState(true);
  const [includeResolution, setIncludeResolution] = useState(true);
  const [checkedMessages, setCheckedMessages] = useState<Set<string>>(
    () => new Set(messages.map(msgId)),
  );
  const [checkedAttachments, setCheckedAttachments] = useState<Set<string>>(
    () => new Set(attachmentRows.map(r => r.id)),
  );
  // '' = not chosen yet (required when several agents are online).
  const [agent, setAgent] = useState<string>('');
  const [sending, setSending] = useState(false);
  // Plan-approval gate: true (default) = Claude presents a plan and waits for
  // a go-ahead in the session before changing anything; false = it states the
  // plan and proceeds. Support questions never change code, so no choice.
  const [planFirst, setPlanFirst] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const hasPlanGate = t.type !== 'question';

  const toggle = (set: Set<string>, id: string, apply: (s: Set<string>) => void) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    apply(next);
  };

  const effectiveAgent = agents.length > 1 ? agent : (agents[0] ?? '');
  const needsAgentPick = agents.length > 1 && !agent;

  // Destination: which product line the dispatch runs under (defaults to the
  // ticket's own) and, optionally, an exact folder on the target machine.
  const ticketLineId: string | null = t.productLineId ?? t.productLine?.id ?? null;
  const [destLineId, setDestLineId] = useState<string | null>(ticketLineId);
  const [destFolder, setDestFolder] = useState<string>('');   // '' = use the mapping
  const [customFolder, setCustomFolder] = useState<string>('');
  const folderOverride = destFolder === '__custom' ? customFolder.trim() : destFolder;
  const lineChanged = destLineId !== ticketLineId;
  const destLineName = destLineId
    ? (productLines?.find(l => l.id === destLineId)?.name ?? t.productLine?.name ?? null)
    : null;

  // Where the chosen machine would open this dispatch, per its server-stored
  // mapping (exact line row, then the agent default). null path = no server
  // mapping — the agent's local config file on that machine decides.
  const mappedRepo = useMemo(() => {
    if (!effectiveAgent || !repoData) return null;
    const rows = repoData.mappings.filter(m => m.agentName === effectiveAgent);
    if (destLineId) {
      const exact = rows.find(m => m.productLineId === destLineId);
      if (exact) return { path: exact.repoPath, via: 'mapping' as const };
    }
    const def = rows.find(m => m.productLineId === null);
    if (def) return { path: def.repoPath, via: 'default' as const };
    return { path: null, via: 'local' as const };
  }, [effectiveAgent, repoData, destLineId]);
  const agentFolders = (effectiveAgent && repoData?.agentRepos[effectiveAgent]) || [];

  const selection: AiDispatchSelection = useMemo(() => {
    const allMsgIds = messages.map(msgId);
    return {
      facts: includeFacts,
      stepsToReproduce: includeSteps,
      resolution: includeResolution,
      messageIds: checkedMessages.size === allMsgIds.length ? null : allMsgIds.filter(id => checkedMessages.has(id)),
      attachmentIds: checkedAttachments.size === attachmentRows.length
        ? null
        : attachmentRows.filter(r => checkedAttachments.has(r.id)).map(r => r.id),
    };
  }, [messages, includeFacts, includeSteps, includeResolution, checkedMessages, checkedAttachments, attachmentRows]);
  const isFullSelection = selection.facts && selection.stepsToReproduce && selection.resolution
    && selection.messageIds === null && selection.attachmentIds === null;

  // The exact server-built prompt for the current selection + mode + line.
  // Re-fetches whenever any of them changes while the preview is open
  // (staleTime is 0 app-wide).
  const { data: previewPrompt, isFetching: previewFetching } = useQuery({
    queryKey: ['ai-dispatch-preview', ticketId, planFirst, destLineId, JSON.stringify(selection)],
    queryFn: () => ticketsApi.aiPromptPreview(ticketId, {
      selection: isFullSelection ? undefined : selection,
      planFirst,
      ...(lineChanged ? { productLineId: destLineId } : {}),
    }),
    enabled: showPreview,
  });

  const handleSend = async () => {
    setSending(true);
    try {
      const r = await ticketsApi.aiDispatch(ticketId, {
        agent: effectiveAgent || undefined,
        selection: isFullSelection ? undefined : selection,
        planFirst,
        ...(lineChanged ? { productLineId: destLineId } : {}),
        ...(folderOverride ? { repoPath: folderOverride } : {}),
      });
      const target = r.agent ? ` on "${r.agent}"` : '';
      if (r.alreadyQueued) {
        toast(`Already queued for Claude${target} — updated with this selection`);
      } else if (r.agentOnline) {
        toast.success(`Sent to Claude${target} — it is opening on that dev machine`);
      } else {
        toast.success(
          `Queued for Claude${target}. The dispatch agent is not running — start it and it will pick this up.`,
          { duration: 6000 },
        );
      }
      onClose();
    } catch {
      toast.error('Failed to send to Claude');
      setSending(false);
    }
  };

  const allOnNone = (
    rows: string[],
    checked: Set<string>,
    apply: (s: Set<string>) => void,
  ) => (
    <span className="flex items-center gap-2 text-xs">
      <button type="button" className="text-primary hover:underline" onClick={() => apply(new Set(rows))}>All</button>
      <span className="text-muted">·</span>
      <button type="button" className="text-primary hover:underline" onClick={() => apply(new Set())}>None</button>
      <span className="text-muted">({checked.size}/{rows.length})</span>
    </span>
  );

  return (
    <Modal open onClose={onClose} title={`Send ${t.ticketNumber} to Claude`} width={620}>
      <p className="text-sm text-muted mb-3">
        Pick what Claude gets to see. The title and report description are always
        included; everything below is optional.
      </p>

      {(hasFacts || hasSteps || hasResolution) && (
        <div className="mb-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted mb-1.5">Ticket details</div>
          <div className="flex flex-col gap-1.5">
            {hasFacts && (
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="checkbox" className="w-4 h-4 accent-primary" checked={includeFacts} onChange={e => setIncludeFacts(e.target.checked)} />
                Environment facts (version, environment, OS, browser, device, reproducibility, impact)
              </label>
            )}
            {hasSteps && (
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="checkbox" className="w-4 h-4 accent-primary" checked={includeSteps} onChange={e => setIncludeSteps(e.target.checked)} />
                Steps to reproduce
              </label>
            )}
            {hasResolution && (
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="checkbox" className="w-4 h-4 accent-primary" checked={includeResolution} onChange={e => setIncludeResolution(e.target.checked)} />
                Recorded resolution
              </label>
            )}
          </div>
        </div>
      )}

      {messages.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted">Conversation</span>
            {allOnNone(messages.map(msgId), checkedMessages, setCheckedMessages)}
          </div>
          <div className="max-h-44 overflow-y-auto rounded-md border border-line-light divide-y divide-line-light">
            {messages.map(m => {
              const id = msgId(m);
              const isNote = m.type === 'internal_note';
              const author = m.authorName || (typeof m.authorId === 'object' ? (m.authorId as any)?.name : null) || 'Unknown';
              return (
                <label key={id} className="flex items-start gap-2 px-2.5 py-1.5 cursor-pointer text-sm hover:bg-row-odd">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-primary mt-0.5 shrink-0"
                    checked={checkedMessages.has(id)}
                    onChange={() => toggle(checkedMessages, id, setCheckedMessages)}
                  />
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5 text-xs text-muted">
                      {isNote ? <Lock className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />}
                      {isNote ? 'Internal note' : 'Reply'} — {author} — {new Date(m.createdAt).toLocaleDateString()}
                    </span>
                    <span className="block truncate text-fg">{snippet(m.content)}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {attachmentRows.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted">Attachments</span>
            {allOnNone(attachmentRows.map(r => r.id), checkedAttachments, setCheckedAttachments)}
          </div>
          <div className="max-h-36 overflow-y-auto rounded-md border border-line-light divide-y divide-line-light">
            {attachmentRows.map(r => (
              <label key={r.id} className="flex items-center gap-2 px-2.5 py-1.5 cursor-pointer text-sm hover:bg-row-odd">
                <input
                  type="checkbox"
                  className="w-4 h-4 accent-primary shrink-0"
                  checked={checkedAttachments.has(r.id)}
                  onChange={() => toggle(checkedAttachments, r.id, setCheckedAttachments)}
                />
                <Paperclip className="w-3.5 h-3.5 text-muted shrink-0" />
                <span className="truncate text-fg">{r.filename}</span>
                <span className="text-xs text-muted shrink-0 ml-auto">{fmtSize(r.size)} · {r.origin}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {hasPlanGate && (
        <div className="mb-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted mb-1.5">Before making changes</div>
          <div className="flex flex-col gap-1.5">
            <label className="flex items-start gap-2 cursor-pointer text-sm">
              <input
                type="radio"
                name="dispatch-mode"
                className="w-4 h-4 accent-primary mt-0.5 shrink-0"
                checked={planFirst}
                onChange={() => setPlanFirst(true)}
              />
              <span>
                <span className="font-medium">Plan first</span> — Claude presents its plan in the
                session and waits for your go-ahead before changing anything.
                <span className="text-muted"> (recommended)</span>
              </span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer text-sm">
              <input
                type="radio"
                name="dispatch-mode"
                className="w-4 h-4 accent-primary mt-0.5 shrink-0"
                checked={!planFirst}
                onChange={() => setPlanFirst(false)}
              />
              <span>
                <span className="font-medium">Proceed directly</span> — Claude states its plan, then
                continues straight into the changes without waiting for approval.
              </span>
            </label>
          </div>
        </div>
      )}

      <div className="mb-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted mb-1.5">Machine</div>
        {agentsLoading ? (
          <p className="text-sm text-muted">Checking which dispatch agents are online…</p>
        ) : agents.length === 0 ? (
          <p className="text-sm text-muted">
            No dispatch agent is online — the ticket will queue until one starts.
          </p>
        ) : agents.length === 1 ? (
          <p className="text-sm">
            Opens on <span className="font-medium">"{agents[0]}"</span> (the only agent online).
          </p>
        ) : (
          <div className="flex flex-col gap-1.5">
            {agents.map(a => (
              <label key={a} className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="radio"
                  name="dispatch-agent"
                  className="w-4 h-4 accent-primary"
                  checked={agent === a}
                  onChange={() => { setAgent(a); setDestFolder(''); setCustomFolder(''); }}
                />
                <Bot className="w-3.5 h-3.5" /> {a}
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="mb-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted mb-1.5">Destination</div>
        <div className="flex items-center gap-2 mb-2">
          <label className="text-sm text-muted shrink-0 w-20">Product line</label>
          <select
            className="select text-sm flex-1 min-w-0"
            value={destLineId ?? ''}
            onChange={e => setDestLineId(e.target.value || null)}
          >
            <option value="">(no product line)</option>
            {ticketLineId && !(productLines ?? []).some(l => l.id === ticketLineId) && (
              <option value={ticketLineId}>{t.productLine?.name ?? 'Ticket’s product line'} — from the ticket</option>
            )}
            {(productLines ?? []).map(l => (
              <option key={l.id} value={l.id}>
                {l.name}{l.id === ticketLineId ? ' — from the ticket' : ''}
              </option>
            ))}
          </select>
        </div>
        {lineChanged && (
          <p className="text-xs text-muted mb-2 ml-[5.5rem]">
            Dispatches under this line instead of the ticket's — the repo mapping and the
            prompt's product framing both follow it. The ticket itself is not changed.
          </p>
        )}
        <div className="flex items-center gap-2 mb-2">
          <label className="text-sm text-muted shrink-0 w-20">Folder</label>
          <select
            className="select text-sm flex-1 min-w-0"
            value={destFolder}
            onChange={e => setDestFolder(e.target.value)}
          >
            <option value="">Use the mapped folder (default)</option>
            {agentFolders.map(p => <option key={p} value={p}>{p}</option>)}
            <option value="__custom">Type a path…</option>
          </select>
        </div>
        {destFolder === '__custom' && (
          <input
            className="input w-full text-sm font-mono mb-2"
            placeholder="C:\projects\my-repo"
            value={customFolder}
            onChange={e => setCustomFolder(e.target.value)}
          />
        )}
        <div className="flex items-start gap-2 text-sm rounded-md border border-line-light bg-row-odd px-2.5 py-2">
          <FolderOpen className="w-4 h-4 text-muted shrink-0 mt-0.5" />
          <span className="min-w-0">
            {folderOverride ? (
              <>
                <span className="text-muted">Opens in </span>
                <span className="font-mono text-xs break-all">{folderOverride}</span>
                <span className="block text-xs text-muted">Folder chosen for this dispatch — overrides all mappings.</span>
              </>
            ) : needsAgentPick ? (
              <span className="text-muted">Pick a machine above to see where this will open.</span>
            ) : !effectiveAgent ? (
              <span className="text-muted">
                No named agent online — whichever machine claims the ticket decides
                (its mappings or local config).
              </span>
            ) : !repoData ? (
              <span className="text-muted">Checking repo mappings…</span>
            ) : mappedRepo?.path ? (
              <>
                <span className="text-muted">Opens in </span>
                <span className="font-mono text-xs break-all">{mappedRepo.path}</span>
                <span className="block text-xs text-muted">
                  {mappedRepo.via === 'mapping'
                    ? `Mapped for "${destLineName}" on "${effectiveAgent}" (Company Settings → Claude Dispatch).`
                    : `"${effectiveAgent}" has no mapping for ${destLineName ? `"${destLineName}"` : 'tickets without a product line'} — this is its default folder.`}
                </span>
              </>
            ) : (
              <span className="text-muted">
                No web-managed mapping for "{effectiveAgent}" — its local config file decides.
                Pick a folder above to be sure.
              </span>
            )}
          </span>
        </div>
      </div>

      <div className="mb-4">
        <button
          type="button"
          className="flex items-center gap-1.5 text-sm text-primary hover:underline"
          onClick={() => setShowPreview(v => !v)}
        >
          {showPreview ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          {showPreview ? 'Hide the prompt' : 'Preview the exact prompt Claude will receive'}
        </button>
        {showPreview && (
          previewPrompt === undefined ? (
            <p className="mt-2 text-sm text-muted">Building the prompt…</p>
          ) : (
            <>
              <pre className={`mt-2 max-h-64 overflow-y-auto rounded-md border border-line-light bg-row-odd p-3 text-xs leading-relaxed whitespace-pre-wrap ${previewFetching ? 'opacity-60' : ''}`}>
                {previewPrompt}
              </pre>
              <p className="mt-1 text-xs text-muted">
                Updates live as you change the selections above. On the dev machine, the dispatch
                agent appends a list of the locally saved attachment files.
              </p>
            </>
          )
        )}
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-line-light">
        <button type="button" className="btn btn-ghost" onClick={onClose} disabled={sending}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSend}
          disabled={sending || needsAgentPick || (destFolder === '__custom' && !customFolder.trim())}
          title={needsAgentPick ? 'Several agents are online — pick whose machine gets the ticket' : undefined}
        >
          <Bot className="w-3.5 h-3.5" /> {sending ? 'Sending…' : 'Send to Claude'}
        </button>
      </div>
    </Modal>
  );
}
